import { useMemo } from 'react';

import { useMappingsState } from '../providers/BulkConfigureContextProvider.tsx';
import { useGetWorkflowValidators } from '../providers/BulkCreateContextProvider.tsx';

import { defaultRequiredFields } from './useGetUnmappedRequiredFields.tsx';

/**
 * @returns Required fields specified via workflow validators that have not been mapped to a column in the bulk configure view
 */
export const useUnmappedWorkflowValidators = (): string[] => {
	const { columnToFieldMappings, optionsIssueTypeFields } = useMappingsState();
	const workflowValidators = useGetWorkflowValidators();

	return useMemo(() => {
		if (!workflowValidators || !optionsIssueTypeFields) {
			return [];
		}

		const mappedFieldIds = new Set(
			columnToFieldMappings
				?.map((mapping) => mapping.field?.fieldId)
				.filter((fieldId): fieldId is string => fieldId !== undefined) ?? [],
		);

		const fieldIdToNameMap = new Map(
			optionsIssueTypeFields.map((field) => [field.fieldId, field.name]),
		);

		const requiredFieldIds = workflowValidators
			.flatMap((validator) => validator.validations ?? [])
			.flatMap((validation) => validation.fieldValidations ?? [])
			.flatMap((fieldValidation) => fieldValidation.fields ?? []);

		// Filter and deduplicate using Set
		const unmappedFieldIds = new Set(
			requiredFieldIds.filter(
				(fieldId) => !mappedFieldIds.has(fieldId) && !defaultRequiredFields.includes(fieldId),
			),
		);

		// Convert to names and use fieldId as a fallback
		return Array.from(unmappedFieldIds).map((fieldId) => fieldIdToNameMap.get(fieldId) ?? fieldId);
	}, [columnToFieldMappings, workflowValidators, optionsIssueTypeFields]);
};
